import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  useCurrentUser,
  useDispatch,
  useUserLabs,
  useUserPractices,
} from '@witmetrics/data-store';
import type { AccountType } from '@witmetrics/utils';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useSearch } from '@/hooks/useSearch';
import Panel from '@/components/Panel';
import SearchField from '@/components/inputs/SearchField';
import AccountCard from '@/components/AccountCard';
import {
  classes,
  fetchAccountTrials,
  filterLabs,
  filterPractices,
} from './utils';

const { LAB, PRACTICE } = ACCOUNT_TYPES;

export type AccountSelectorProps = {
  onSelectAccount: (accountType: AccountType, accountId: number) => void;
};

export default function AccountSelector({
  onSelectAccount,
}: AccountSelectorProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const currentUser = useCurrentUser();
  const userPractices = useUserPractices(currentUser?.id);
  const userLabs = useUserLabs(currentUser?.id);
  const [search, setSearch] = useSearch();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const trials = await fetchAccountTrials();
      dispatch.addAccountTrials(trials);
    } catch (err) {
      onApiError(err, 'Error loading account trials');
    }
  };

  const renderColumn = (
    title: string,
    accountType: AccountType,
    items: { id: number; name: string }[]
  ) => (
    <div>
      <div className={classes.colHead}>
        <span className={classes.colLabel}>{title}</span>
        <span className={classes.colCount}>{items.length}</span>
      </div>
      <div className={classes.list}>
        {items.map(({ id, name }) => (
          <AccountCard
            key={id}
            practiceId={accountType === PRACTICE ? id : undefined}
            name={name}
            onClick={() => onSelectAccount(accountType, id)}
          />
        ))}
      </div>
    </div>
  );

  const hasPractices = !isEmpty(userPractices);
  const hasLabs = !isEmpty(userLabs);
  const isDual = hasPractices && hasLabs;

  return (
    <Panel className={classes.panel} side="right">
      <SearchField
        className={classes.search}
        value={search}
        onChange={setSearch}
      />
      <div
        className={twMerge(
          classes.grid,
          isDual ? classes.gridDual : classes.gridSingle
        )}>
        {hasPractices &&
          renderColumn(
            'Practices',
            PRACTICE,
            filterPractices(userPractices, search)
          )}
        {hasLabs && renderColumn('Labs', LAB, filterLabs(userLabs, search))}
      </div>
    </Panel>
  );
}
